/**
 * ErrorResolutionPage — `/mail/errors`.
 *
 * Live operator surface backed by service-mail `/v1/mail-errors/*`.
 *
 * Queue table with status filter pills + per-row actions: "Assign to me"
 * (status → INVESTIGATING), "Resolve" + "Dismiss" (open a notes modal and
 * POST to the corresponding endpoint).
 */
import { type JSX, useEffect, useState } from 'react';
import { AlertTriangle, Check, Loader2, UserCheck, X } from 'lucide-react';

import { PageHeader } from '../shared/StaffUI';
import { decodeStaffClaims } from '../../lib/auth/jwt';
import { getToken } from '../../lib/auth/tokenStore';
import {
  assignMailError,
  dismissMailError,
  listMailErrors,
  resolveMailError,
  type MailErrorDto,
  type MailErrorSource,
  type MailErrorStatus,
} from '../../lib/api/mailErrors';

const STATUS_OPTIONS: { value: MailErrorStatus | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'OPEN', label: 'Open' },
  { value: 'INVESTIGATING', label: 'Investigating' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'DISMISSED', label: 'Dismissed' },
];

const SOURCE_OPTIONS: { value: MailErrorSource | ''; label: string }[] = [
  { value: '', label: 'All sources' },
  { value: 'SCANNER', label: 'Scanner' },
  { value: 'OCR', label: 'OCR' },
  { value: 'VALIDATION', label: 'Validation' },
  { value: 'PARTNER_INTEGRATION', label: 'Partner integration' },
  { value: 'OTHER', label: 'Other' },
];

function statusPillClass(status: MailErrorStatus): string {
  switch (status) {
    case 'OPEN': return 'bg-red-50 text-red-700';
    case 'INVESTIGATING': return 'bg-yellow-50 text-yellow-700';
    case 'RESOLVED': return 'bg-green-50 text-green-700';
    case 'DISMISSED': return 'bg-gray-100 text-gray-600';
  }
}

interface ResolveModalState {
  entry: MailErrorDto;
  action: 'RESOLVE' | 'DISMISS';
}

export function ErrorResolutionPage(): JSX.Element {
  const [items, setItems] = useState<readonly MailErrorDto[]>([]);
  const [statusFilter, setStatusFilter] = useState<MailErrorStatus | ''>('OPEN');
  const [sourceFilter, setSourceFilter] = useState<MailErrorSource | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [modal, setModal] = useState<ResolveModalState | null>(null);
  const [notes, setNotes] = useState('');

  const claims = decodeStaffClaims(getToken());
  const myOperatorId = claims?.subject ?? '';

  async function refresh(): Promise<void> {
    setLoading(true);
    try {
      const res = await listMailErrors({
        ...(statusFilter && { status: statusFilter }),
        ...(sourceFilter && { source: sourceFilter }),
        pageSize: 50,
      });
      setItems(res.items);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load mail errors');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // note: deps intentionally narrow — rule not enabled in this config
  }, [statusFilter, sourceFilter]);

  async function assignToMe(entry: MailErrorDto): Promise<void> {
    if (!myOperatorId) {
      setError('Cannot assign — no current operator JWT');
      return;
    }
    setBusyId(entry.id);
    try {
      await assignMailError(entry.id, myOperatorId);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Assign failed');
    } finally {
      setBusyId(null);
    }
  }

  function openModal(entry: MailErrorDto, action: 'RESOLVE' | 'DISMISS'): void {
    setNotes('');
    setModal({ entry, action });
  }

  async function submitModal(): Promise<void> {
    if (!modal) return;
    if (notes.trim().length === 0) return;
    setBusyId(modal.entry.id);
    try {
      if (modal.action === 'RESOLVE') {
        await resolveMailError(modal.entry.id, notes.trim());
      } else {
        await dismissMailError(modal.entry.id, notes.trim());
      }
      setModal(null);
      setNotes('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-5" data-testid="error-resolution-page">
      <PageHeader
        title="Error Resolution Queue"
        description="Failed scans, OCR mismatches, validation rejects, and partner-integration errors."
      />

      <div className="bg-white border border-border rounded-xl p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label htmlFor="status-filter" className="text-xs font-semibold text-gray-700">Status</label>
          <select
            id="status-filter"
            data-testid="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as MailErrorStatus | '')}
            className="px-2 py-1.5 text-sm border border-border rounded-lg"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="source-filter" className="text-xs font-semibold text-gray-700">Source</label>
          <select
            id="source-filter"
            data-testid="source-filter"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as MailErrorSource | '')}
            className="px-2 py-1.5 text-sm border border-border rounded-lg"
          >
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <span className="text-xs text-muted ml-auto">{items.length} entries</span>
      </div>

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>
      ) : null}

      {loading ? (
        <div className="bg-white border border-border rounded-xl p-8 text-center text-sm text-muted">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-8 text-center" data-testid="errors-empty">
          <AlertTriangle className="w-8 h-8 text-muted mx-auto mb-2" />
          <p className="text-sm font-semibold text-gray-700">Queue clear</p>
          <p className="text-xs text-muted mt-1">No matching mail errors.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="errors-list">
          {items.map((it) => {
            const terminal = it.status === 'RESOLVED' || it.status === 'DISMISSED';
            return (
              <li
                key={it.id}
                data-testid={`error-row-${it.id}`}
                className="bg-white border border-border rounded-xl p-4 flex items-start justify-between gap-4"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-block px-2 py-0.5 text-[10px] font-bold rounded bg-blue-50 text-blue-700">
                      {it.source.replace(/_/g, ' ')}
                    </span>
                    <span className="inline-block px-2 py-0.5 text-[10px] font-bold rounded bg-purple-50 text-purple-700 font-mono">
                      {it.kind}
                    </span>
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded ${statusPillClass(it.status)}`}>
                      {it.status}
                    </span>
                    {it.subjectId ? (
                      <span className="text-[11px] text-muted font-mono">{it.subjectType}:{it.subjectId}</span>
                    ) : null}
                  </div>
                  <p className="text-sm text-gray-900 mt-2">{it.details}</p>
                  <p className="text-xs text-muted mt-1">
                    By {it.operatorId}
                    {it.outletCode ? ` · ${it.outletCode}` : ''}
                    {' · '}
                    {new Date(it.createdAt).toLocaleString()}
                    {it.assignedToOperatorId ? ` · assigned ${it.assignedToOperatorId}` : ''}
                  </p>
                  {terminal && it.resolutionNotes ? (
                    <p className="text-xs text-muted mt-1 italic">Notes: {it.resolutionNotes}</p>
                  ) : null}
                </div>
                {!terminal ? (
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => void assignToMe(it)}
                      disabled={busyId === it.id}
                      data-testid={`assign-${it.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border text-xs font-semibold rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      <UserCheck className="w-3.5 h-3.5" /> Assign to me
                    </button>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => openModal(it, 'RESOLVE')}
                        disabled={busyId === it.id}
                        data-testid={`resolve-${it.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-nipost-green text-white text-xs font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
                      >
                        <Check className="w-3.5 h-3.5" /> Resolve
                      </button>
                      <button
                        type="button"
                        onClick={() => openModal(it, 'DISMISS')}
                        disabled={busyId === it.id}
                        data-testid={`dismiss-${it.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-300 text-red-700 text-xs font-semibold rounded-lg hover:bg-red-50 disabled:opacity-50"
                      >
                        <X className="w-3.5 h-3.5" /> Dismiss
                      </button>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {modal ? (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          data-testid="resolve-modal"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              {modal.action === 'RESOLVE' ? 'Resolve error' : 'Dismiss error'}
            </h2>
            <p className="text-sm text-gray-700 mb-3">{modal.entry.details}</p>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Notes (required)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="resolve-notes"
              rows={3}
              className="w-full px-2 py-1.5 text-sm border border-border rounded-lg"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => { setModal(null); setNotes(''); }}
                className="px-3 py-1.5 text-sm font-semibold border border-border rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void submitModal()}
                disabled={notes.trim().length === 0}
                data-testid="resolve-submit"
                className={`px-3 py-1.5 text-sm font-semibold rounded-lg text-white ${
                  modal.action === 'RESOLVE'
                    ? 'bg-nipost-green hover:bg-nipost-green-dark'
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50`}
              >
                {modal.action === 'RESOLVE' ? 'Resolve' : 'Dismiss'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
